import {
  Box,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import {
  Home20Px,
  ProgressButton,
  useToast,
  DialogRoot,
  AlertBanner
} from "@qubit/autoparts";

import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppSelector } from "~/app/store";

import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import { StartPickingNextBatchButton } from "~/features/autostorePicking/StartPickingNextBatchButton";

import {
  mixpanelStartPickingWrapper,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectSelectedPortId,
  selectThisWorkstation
} from "~/redux/selectors/workstationsSelectors";
import { useGetStatusCountQuery } from "~/redux/warehouse/batches.hooks";

import SmallNumberStat from "./SmallNumberStat";

import TempZoneAlertBanner from "./TempZoneAlertBanner";
import { useWorkstationPickingConfiguration } from "./useWorkstationPickingConfiguration";

type StartPickingModalProps = {
  open: boolean;
  onClose: () => void;
  maxWidth?: "xs" | "sm" | "md" | "lg";
  onNextPickButtonClickCallback: () => Promise<string | null>;
  onHomeButtonClickCallback: () => (Promise<void> | void) | null;
  batchName?: string;
  title: string;
  text?: string;
  pickButtonText: string;
  isConfigurePortsRetrying?: boolean;
  multiPortRequestDuration?: number;
};

export function StartPickingModal(props: StartPickingModalProps) {
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const { pathname } = useLocation();

  const {
    open,
    onClose,
    maxWidth,
    onHomeButtonClickCallback,
    onNextPickButtonClickCallback,
    batchName,
    title,
    text,
    pickButtonText,
    isConfigurePortsRetrying,
    multiPortRequestDuration
  } = props;

  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const cartAssignmentEnabled =
    useAppSelector(selectServerConfig).cartAssignmentEnabled;
  const portId = useAppSelector(selectSelectedPortId);

  const isMultiportRetryMode =
    isConfigurePortsRetrying ||
    (!!multiPortRequestDuration && multiPortRequestDuration > 1000);
  const trackedModalName = useMixpanelModalName("Start Picking");

  const isOnHomePage = pathname.includes("/autostore-main");
  const isClosePrevented = isMultiportRetryMode || !isOnHomePage;

  const { isStartPickingBlocked } = useWorkstationPickingConfiguration();
  const {
    data: batchCount,
    isLoading,
    isError,
    error
  } = useGetStatusCountQuery(
    siteWorkstation && open
      ? {
          gridId: siteWorkstation?.autostoreGridId,
          portId
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true
    }
  );
  const { palette } = useTheme();

  useEffect(() => {
    if (error) {
      errorToast(getMessageFromRtkError(error), {
        trackingLocation: trackedModalName
      });
    }
  }, [error, errorToast, trackedModalName]);

  function onBackdropClicked() {
    if (!isClosePrevented) {
      onClose();
    }
  }

  return (
    <DialogRoot
      open={open}
      onClose={onBackdropClicked}
      maxWidth={maxWidth}
      fullWidth
      disableEscapeKeyDown={isClosePrevented}
    >
      <Paper>
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: "24px" }}>
            {title}
          </Typography>

          {batchName && (
            <Typography sx={{ fontSize: ".8em" }}>
              {`Batch Name: ${batchName}`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: text ? 1 : 0
          }}
        >
          {text && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "1.25em"
              }}
            >
              {text}
            </Typography>
          )}

          <Stack
            aria-label="batch-status-count"
            flexDirection="row"
            gap={1}
            alignItems="center"
            justifyContent="center"
            mt={2}
          >
            {isLoading && (
              <Skeleton variant="rectangular" width="100%" height={56} />
            )}
            {!isError &&
              batchCount &&
              Object.keys(batchCount)
                .sort()
                .map((key) => {
                  let textToDisplay = "";
                  let someColor = palette.darkGray.main;
                  let backgroundColor = "white";

                  switch (key) {
                    case "cartReady":
                      if (!cartAssignmentEnabled) return null;
                      textToDisplay = t("cart ready");
                      someColor = palette.primary.main;
                      backgroundColor = palette.primary.light;
                      break;
                    case "scheduled":
                      textToDisplay = t("scheduled");

                      if (!cartAssignmentEnabled) {
                        someColor = palette.primary.main;
                        backgroundColor = palette.primary.light;
                      }
                      break;
                    case "suspended":
                      if (batchCount[key] === 0) return null;
                      textToDisplay = t("suspended");
                      break;
                    default:
                      return null;
                  }

                  return (
                    <SmallNumberStat
                      key={key}
                      label={textToDisplay}
                      number={batchCount[key]}
                      labelColor={someColor}
                      numberColor={someColor}
                      backroundColor={backgroundColor}
                    />
                  );
                })}
          </Stack>

          {isMultiportRetryMode && (
            <ASDSThemeProvider>
              <AlertBanner
                severity="info"
                sx={{ mt: 4 }}
                collapseSx={{ width: "100%" }}
              >
                {t("please wait switching workstation mode")}
              </AlertBanner>
            </ASDSThemeProvider>
          )}

          <TempZoneAlertBanner />

          <Box
            width="100%"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(auto, 220px))",
              justifyContent: "space-between",
              gap: 2,
              mt: "4vh"
            }}
          >
            <ProgressButton
              buttonSize="xLarge"
              emphasis="high"
              responsive
              variant="contained"
              color="secondary"
              sx={{ textTransform: "capitalize" }}
              fullWidth
              startIcon={
                <Home20Px style={{ opacity: isMultiportRetryMode ? 0.5 : 1 }} />
              }
              disabled={isMultiportRetryMode}
              onClick={(): (Promise<void> | void) | null => {
                mixpanelStartPickingWrapper(
                  trackedModalName,
                  "Button Click",
                  "Home",
                  batchCount
                );

                void onHomeButtonClickCallback();
              }}
            >
              {t("nav.link.home")}
            </ProgressButton>

            <StartPickingNextBatchButton
              disabled={isStartPickingBlocked}
              startBatchCallback={async () => {
                mixpanelStartPickingWrapper(
                  trackedModalName,
                  "Button Click",
                  "Start/Next Pick",
                  batchCount
                );
                return await onNextPickButtonClickCallback();
              }}
            >
              {pickButtonText}
            </StartPickingNextBatchButton>
          </Box>
        </DialogContent>
      </Paper>
    </DialogRoot>
  );
}
